import { IEligibility } from '@v2/domains/eligibilities/models/eligibilities.model'
import parsePhoneNumber from 'libphonenumber-js'

import { AssociatedBenefitContractLinkDto } from 'src/api/generated'

import { ITag } from '../../../tag/tag.types'
import { EnumDocumentType, IDocument } from '../../types/document.types'
import { IEnrollment } from '../../types/enrollment.types'
import { IUser, SinglePerson } from '../../types/person.types'
import {
  BASIC_PERSON_INITIAL_DATA,
  ID_METLIFE_BENEFIT,
} from './singlePersonConfig'

interface EnrichedPerson extends SinglePerson {
  customerType?: 'account' | 'lead'
  allowScheduleAppointment: boolean
  _isActiveInIdg: boolean
  metlifeID: string | null
  driverTagsList: string[]
  abcs: AssociatedBenefitContractLinkDto[]
  getTagColorByTagName: (tagName: string) => string
  formattedPhoneNumber: string
}

class SinglePersonDto implements Partial<EnrichedPerson> {
  private _customerType: 'account' | 'lead'

  id?: string
  email?: string
  firstName?: string
  isVerified?: boolean
  lastName?: string
  birthDate?: string | null
  companies?: string[]
  nationBuilderId?: string | null
  gender?: string | null
  phoneNumber?: string
  otherPhoneNumber?: string[]
  street?: string | null
  city?: string | null
  state?: string | null
  postalCode?: string | null
  source?: string | null
  haveHealthInsurance?: string | null
  rating?: string | null
  preferredLanguage?: string
  otherPreferredLanguage?: string | null
  country?: string | null
  sfdcId?: string | null
  otherEmail?: string[]
  enrollments?: IEnrollment[]
  eligibilities?: IEligibility[] | []
  documents?: IDocument[]
  tags?: ITag[] | []
  assignedTo?: Omit<IUser, 'firstName' | 'lastName'>
  createdBy?: IUser
  updatedBy?: IUser;
  [key: string]: any

  constructor(person: Partial<SinglePerson>) {
    Object.assign(this, person)
    this._customerType = this.isVerified ? 'account' : 'lead'
  }

  get customerType() {
    return this._customerType
  }

  get _isActiveInIdg() {
    return (
      this.enrollments?.some(
        (enrollment: IEnrollment) =>
          enrollment.associatedBenefitContract.name.includes('BCF - NY') &&
          enrollment.isVerified,
      ) ?? false
    )
  }

  get allowScheduleAppointment() {
    return this.customerType === 'account' && this._isActiveInIdg
  }

  get driversLicenseNumber() {
    return this.documents?.find(
      (doc) => doc.type === EnumDocumentType.DRIVER_LICENSE,
    )?.number
  }

  get tlcLicenseNumber() {
    return this.documents?.find((doc) => doc.type === EnumDocumentType.TLC)
      ?.number
  }

  get metlifeID() {
    const metlifeEligible = this.eligibilities?.find(
      (eligibility) => eligibility?.benefit?.id === ID_METLIFE_BENEFIT,
    )

    if (metlifeEligible) {
      return !metlifeEligible.providerExternalId ||
        metlifeEligible.providerExternalId?.includes('mock')
        ? 'Pending'
        : metlifeEligible.providerExternalId
    }
    return null
  }

  get abcs() {
    return this.enrollments?.map(
      (enrollment) => enrollment.associatedBenefitContract,
    )
  }

  get driverTagsList() {
    return (
      this.tags?.filter(({ isHided }) => !isHided)?.map(({ name }) => name) ??
      []
    )
  }

  get fullName() {
    return this.firstName + ' ' + this.lastName
  }

  get formattedPhoneNumber() {
    return parsePhoneNumber(this.phoneNumber || '', 'US')?.formatNational()
  }

  getTagColorByTagName = (tagName: string) => {
    const tag = this.tags?.find(({ name }) => name === tagName)
    return tag ? tag.color ?? '' : ''
  }

  getMutationFields() {
    return Object.keys(BASIC_PERSON_INITIAL_DATA).reduce<
      typeof BASIC_PERSON_INITIAL_DATA
    >((mutationData, key) => {
      mutationData[key as keyof typeof BASIC_PERSON_INITIAL_DATA] = this[key]
      return mutationData
    }, {})
  }
}

export default SinglePersonDto
